import React from "react";
import { useNavigate, useLocation } from "react-router";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";

import Header from "src/components/wizard/Header";
import SearchItems from "./SearchItems";
import { fullWidthDialog } from "src/constants/dialog";

export default function ItemLookup({ project, createAssetLoading, onCreateAsset, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isReferenceMode = project.referenceAssets || project.importedAssets;
  const title = isReferenceMode ? "SKU Library" : "Item Details";
  const showReceiveReturnedItem = !!project.addPreviouslyReceivedAssetsToShipments;

  const handleAddNewSKU = () => {
    const params = new URLSearchParams(location.search);
    params.set("duplicate", "true");
    params.set("newItem", "true");
    navigate({ search: params.toString() });
    // Create regular Asset as working copy - SKU will be created on Save
    onCreateAsset({ duplicate: true });
  };

  const handleAddNewOneOffItem = () => {
    const params = new URLSearchParams(location.search);
    params.set("newItem", "true");
    navigate({ search: params.toString() });
    onCreateAsset({ skuNumber: "One-Off Item" });
  };

  const handleOpenReceivedItems = searchTerm => {
    const params = new URLSearchParams(location.search);
    params.delete("itemLookup");
    params.set("receivedItems", "true");
    if (searchTerm) {
      params.set("receivedItemsSearch", searchTerm);
    } else {
      params.delete("receivedItemsSearch");
    }
    navigate({ search: params.toString() });
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      sx={{
        ...fullWidthDialog,
        "& .MuiDialog-paper": {
          height: "min(760px, calc(100vh - 48px))",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
        },
      }}
    >
      <Box bgcolor="#f4f6f8">
        <Header
          title={title}
          info={
            <Box display="flex" alignItems="center" gap={1}>
              <PopupState variant="popover" popupId="item-select">
                {popupState => (
                  <React.Fragment>
                    <React.Fragment>
                      <ButtonBase variant="contained" {...bindTrigger(popupState)}>
                        {createAssetLoading && <CircularProgress size={18} sx={{ mr: 1 }} />}
                        More Options
                      </ButtonBase>
                      <Menu {...bindMenu(popupState)}>
                        <MenuItem onClick={handleAddNewSKU}>
                          <AddIcon fontSize="small" sx={{ mr: 1 }} />
                          Add New SKU
                        </MenuItem>
                        <MenuItem onClick={handleAddNewOneOffItem}>
                          <AddIcon fontSize="small" sx={{ mr: 1 }} />
                          Add New One-Off Item
                        </MenuItem>
                        {showReceiveReturnedItem && (
                          <MenuItem onClick={handleOpenReceivedItems}>
                            <AddIcon fontSize="small" sx={{ mr: 1 }} />
                            Receive Returned Item
                          </MenuItem>
                        )}
                      </Menu>
                    </React.Fragment>
                  </React.Fragment>
                )}
              </PopupState>
            </Box>
          }
        />
      </Box>
      <DialogContent
        sx={{
          background: "#f4f6f8",
          px: { xs: 0, sm: 3 },
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <Box display="flex" justifyContent="center" px={{ xs: 2, sm: 0 }} py={1}>
          <Box
            component="img"
            borderRadius={3}
            sx={{
              display: "block",
              height: 88,
              width: "auto",
              maxWidth: "100%",
              objectFit: "contain",
            }}
            src="/item-lookup.png"
          />
        </Box>
        <Typography fontWeight={400} align="center" fontSize={17} px={{ xs: 2, sm: 0 }}>
          Select the item you received from the list below
        </Typography>
        <Box mt={2} flex={1} minHeight={0}>
          <SearchItems
            project={project}
            onOpenReceivedItems={showReceiveReturnedItem ? handleOpenReceivedItems : undefined}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
